/**
 * @sunday/incart: what an Add button says once its item is in the cart (AR, 17 Oct 2026).
 * No pop-up: the button itself shows the cart.
 *
 * A button opts in with data attributes, set by the module that owns it whenever the choice changes,
 * followed by paint(btn):
 *   data-sd-v       the variant id
 *   data-sd-kind    "m" a mattress, "a" an accessory
 *   data-sd-custom  a mattress cut to a custom size: the line's Custom Size text ("" for a standard size)
 *
 *   m  that size already in the cart → "− n +" and "Go to cart · price" take the button's place (27 Oct): + adds another bed,
 *      − takes one out; at 0 the Add button is back.
 *   a  n of that size in the cart → the button steps aside and "− n in cart +" takes its place:
 *      + adds one more (through the button's own Add), − takes one out; at 0 the Add button is back.
 *      The accessory page's own Add (data-sd-go) shows the same marigold pill as a mattress (27 Oct); below, the dock's way:
 *      data-sd-go (the accessory page's Add and its phone dock): "Cart · ₹total ›" shows beside the steps,
 *      so the way on to the cart is right there (AR, 20 Oct 2026). In the dock the steps show the number only.
 *
 * The cart is read once on a page that has such a button, kept from every sunday:cart, and read again when the
 * browser brings the page back (the back button from the cart), so a return to the page shows what is in the cart.
 */
import * as cart from '@sunday/cart';
import { t as T } from '@sunday/text';
import { esc, money } from '@sunday/util';

let now = null;
const cartUrl = () => (window.Sunday && window.Sunday.cartUrl) || '/cart';
const prop = (l, k) => ((l.properties || {})[k] || '').toString();

function linesFor(v, custom) {
  if (!now || !v) return [];
  return now.items.filter((l) => Number(l.variant_id) === Number(v) && (custom == null || prop(l, 'Custom Size') === custom));
}

const all = () => document.querySelectorAll('[data-sd-v]');

export function paint(b) {
  if (!b || !b.isConnected) return;
  if (!now) return load(); // painted once the cart answers
  const kind = b.dataset.sdKind;
  if (kind === 'm') return paintMattress(b);
  if (kind === 'a') return paintAcc(b);
}

/* ---------- a mattress: "− n +" and "Go to cart · price" take the button's place ---------- */
/* 27 Oct (AR go): that size already in the cart → the Add button is replaced by one marigold pill: − n + on the left
   (+ adds another bed and stays on the page, − takes one out), "Go to cart · price" in the middle (the price of one bed,
   read from the button's own label). At 0 the Add button is back. Not in the phone's bottom bar (it stays one row). */
function paintMattress(b) {
  const custom = b.dataset.sdCustom || '';
  const n = b.dataset.sdV ? linesFor(b.dataset.sdV, custom).reduce((a, l) => a + l.quantity, 0) : 0;
  if (b.closest('[data-dock], .sd-mbar')) return;
  paintPill(b, n);
}

/* One look for a bed and an accessory page's own Add: the marigold pill (27 Oct, AR: uniform). */
function paintPill(b, n) {
  let s = b.__sdStep;
  if (!n) {
    if (s) {
      s.hidden = true;
      b.hidden = false;
    }
    return;
  }
  if (!s) {
    s = document.createElement('div');
    s.className = 'sd-incart sd-incart-m';
    s.setAttribute('role', 'group');
    s.innerHTML = `<span class="sd-im-q"><button type="button" data-sd-less aria-label="${esc(T('sheet.one_less'))}">−</button><span aria-live="polite" data-sd-n></span><button type="button" data-sd-more aria-label="${esc(T('sheet.one_more'))}">+</button></span><a class="sd-im-go" href="${esc(cartUrl())}"></a>`;
    s.__sdBtn = b;
    b.__sdStep = s;
    b.after(s);
  }
  // The price of one bed, from the button's label ("Add to cart · ₹34,999"); kept while the label says "Adding…".
  if (!b.classList.contains('sd-adding')) {
    const parts = (b.textContent || '').split('·');
    let pr = parts.length > 1 ? parts[parts.length - 1].trim() : '';
    // The accessory page shows its price beside the button: the pill says only Go to cart.
    if ('apAdd' in b.dataset) pr = '';
    if (/\d/.test(pr)) s.__sdPrice = pr;
  }
  s.querySelector('[data-sd-n]').textContent = String(n);
  s.querySelector('.sd-im-go').textContent = T('sheet.go_cart') + (s.__sdPrice ? ' · ' + s.__sdPrice : '');
  s.removeAttribute('aria-busy');
  const had = document.activeElement === b;
  s.hidden = false;
  b.hidden = true;
  if (had) s.querySelector('[data-sd-more]').focus({ preventScroll: true });
}

/* ---------- an accessory: − n in cart + ---------- */
function paintAcc(b) {
  const n = linesFor(b.dataset.sdV, null).reduce((a, l) => a + l.quantity, 0);
  // The accessory page's own Add (not the phone dock, not a collection card): the same pill as a mattress.
  if (('sdGo' in b.dataset || 'apAdd' in b.dataset) && !b.closest('[data-dock]')) return paintPill(b, n);
  let s = b.__sdStep;
  if (!n) {
    if (s) {
      s.hidden = true;
      b.hidden = false;
      dockIn(s);
    }
    if (b.__sdGo) b.__sdGo.hidden = true;
    return;
  }
  if (!s) {
    s = document.createElement('div');
    s.className = 'sd-incart';
    s.setAttribute('role', 'group');
    s.innerHTML = `<button type="button" data-sd-less aria-label="${esc(T('sheet.one_less'))}">−</button><span aria-live="polite" data-sd-n></span><button type="button" data-sd-more aria-label="${esc(T('sheet.one_more'))}">+</button>`;
    s.__sdBtn = b;
    b.__sdStep = s;
    b.after(s);
  }
  if (!b.hidden) {
    // Take the button's place: its height, and its width when it fills its row.
    const cs = getComputedStyle(b);
    s.style.minHeight = Math.max(44, b.offsetHeight) + 'px';
    s.style.flex = cs.flex;
    const full = b.parentElement && b.offsetWidth >= b.parentElement.clientWidth - parseFloat(getComputedStyle(b.parentElement).paddingLeft) - parseFloat(getComputedStyle(b.parentElement).paddingRight) - 2;
    s.classList.toggle('sd-incart-full', !!full);
    s.style.minWidth = full ? '' : b.offsetWidth + 'px';
  }
  const inDock = !!b.closest('[data-dock]');
  s.classList.toggle('sd-incart-dock', inDock);
  if (inDock) {
    s.style.minWidth = '';
    s.style.flex = '';
    s.classList.remove('sd-incart-full');
  }
  s.querySelector('[data-sd-n]').textContent = inDock ? String(n) : T('sheet.n_in_cart', { n });
  if ('sdGo' in b.dataset) {
    let g = b.__sdGo;
    if (!g) {
      g = document.createElement('a');
      g.className = 'sd-btn sd-incart-go' + (inDock ? '' : ' sd-full');
      g.href = cartUrl();
      b.__sdGo = g;
      s.after(g);
    }
    g.textContent = T('sheet.cart_total', { total: money(now.total_price) }) + ' ›';
    g.hidden = false;
  }
  s.removeAttribute('aria-busy');
  const had = document.activeElement === b;
  s.hidden = false;
  b.hidden = true;
  dockIn(s);
  if (had) s.querySelector('[data-sd-more]').focus({ preventScroll: true });
}

/* 21 Oct (older phones): the dock says when its stepper shows (.sd-dock-in), for browsers without :has(). */
function dockIn(s) {
  const d = s.closest('.sd-dock');
  if (d) d.classList.toggle('sd-dock-in', s.classList.contains('sd-incart-dock') && !s.hidden);
}

document.addEventListener('click', async (e) => {
  const s = e.target.closest('.sd-incart');
  if (!s || s.getAttribute('aria-busy') === 'true') return;
  const b = s.__sdBtn;
  if (!b) return;
  if (e.target.closest('[data-sd-more]')) {
    s.setAttribute('aria-busy', 'true');
    // The button's own Add puts one more in (same bed rules as the first one). A mattress stays on the page.
    if (s.classList.contains('sd-incart-m') && b.dataset.sdKind === 'm') b.dataset.sdStay = '1';
    b.click();
    setTimeout(() => s.removeAttribute('aria-busy'), 4000);
    return;
  }
  if (e.target.closest('[data-sd-less]')) {
    const ls = linesFor(b.dataset.sdV, s.classList.contains('sd-incart-m') && b.dataset.sdKind === 'm' ? b.dataset.sdCustom || '' : null);
    if (!ls.length) return;
    // Take one out: from a line not tied to a bed first, else the last one added.
    const l = ls.find((x) => !prop(x, '_bed')) || ls[ls.length - 1];
    s.setAttribute('aria-busy', 'true');
    try {
      await cart.setQty(l.key, l.quantity - 1);
    } catch (err) {
      s.removeAttribute('aria-busy');
    }
  }
});

/* ---------- keep every button in step with the cart ---------- */
function set(c) {
  if (!c || !c.items) return;
  now = c;
  all().forEach(paint);
}
document.addEventListener('sunday:cart', (e) => set(e.detail && e.detail.cart));
document.addEventListener('sunday:add-failed', () => document.querySelectorAll('.sd-incart[aria-busy]').forEach((s) => s.removeAttribute('aria-busy')));
window.addEventListener('pageshow', (e) => {
  if (e.persisted) cart.refresh().catch(() => {});
});
// The cart is read only on pages with an Add that follows it (the first paint asks for it), and after back or
// forward to any page: the browser may show the page as it was (old cart count), so the page is told the cart now.
let asked = null;
function load() {
  if (!asked) asked = cart.current().then(set).catch(() => {});
  return asked;
}
const back = (performance.getEntriesByType && performance.getEntriesByType('navigation')[0]?.type) === 'back_forward';
if (back) asked = cart.refresh().catch(() => {});
